import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesApiService, SalesChannelRecord, SalesReservationRecord } from './sales-api.service';

type StatusOption = { value: string; labelKey: string };

@Component({
  standalone: true,
  selector: 'ain-sales-reservations',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule, AinDatePipe],
  template: `
    <ain-page [title]="t('sales.reservations.title')" [description]="t('sales.reservations.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.reservations.action.expireDue')" severity="secondary" (click)="expireDue()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('sales.filter.hide') : t('sales.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('sales.reservations.fields.reservationKey') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.reservationKey" (ngModelChange)="applyFilters()" />
              </label>
              <label>
                <span>{{ t('columns.status') }}</span>
                <p-select [options]="statusOptions" optionValue="value" [(ngModel)]="filters.status" [showClear]="true" appendTo="body" (ngModelChange)="applyFilters()">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('sales.orders.fields.channel') }}</span>
                <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="filters.salesChannelId" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
              </label>
            </div>
          }

          <p-table
            [value]="rows()"
            dataKey="id"
            [paginator]="true"
            [rows]="15"
            [selection]="selectedRows()"
            (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.reservations.fields.reservationKey') }}</th>
                <th>{{ t('sales.reservations.fields.reservationType') }}</th>
                <th>{{ t('sales.reservations.fields.quantity') }}</th>
                <th>{{ t('sales.reservations.fields.expiresAt') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td>{{ row.reservationKey }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ typeLabel(row.reservationType) }}</button></td>
                <td>{{ row.reservedQuantity }}</td>
                <td>{{ row.expiresAtUtc | ainDate:'datetime' }}</td>
                <td>{{ statusLabel(row.status) }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (message()) {
            <p class="message">{{ message() }}</p>
          }
          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .message { margin: 0; color: #047857; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class SalesReservationsComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly message = signal('');
  readonly showFilters = signal(true);
  readonly sourceRows = signal<SalesReservationRecord[]>([]);
  readonly rows = signal<SalesReservationRecord[]>([]);
  readonly selectedRows = signal<SalesReservationRecord[]>([]);
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly filters = { reservationKey: '', status: '' as string | null, salesChannelId: '' as string | null };

  readonly statusOptions: StatusOption[] = [
    { value: 'Active', labelKey: 'sales.reservations.status.active' },
    { value: 'Released', labelKey: 'sales.reservations.status.released' },
    { value: 'Expired', labelKey: 'sales.reservations.status.expired' },
    { value: 'Consumed', labelKey: 'sales.reservations.status.consumed' }
  ];

  ngOnInit(): void {
    this.api.listChannels().subscribe({
      next: rows => this.channels.set(rows),
      error: () => this.channels.set([])
    });
    this.load();
  }

  channelOptions(): { label: string; value: string }[] {
    return this.channels().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.message.set('');
    this.api.listReservations({
      status: this.filters.status ?? undefined,
      salesChannelId: this.filters.salesChannelId || undefined
    }).subscribe({
      next: reservations => {
        this.sourceRows.set(reservations);
        this.applyFilters();
        this.loading.set(false);
      },
      error: () => {
        this.sourceRows.set([]);
        this.rows.set([]);
        this.error.set(this.t('sales.reservations.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  applyFilters(): void {
    const key = this.filters.reservationKey.trim().toLowerCase();
    this.rows.set(
      this.sourceRows().filter(row =>
        !key || row.reservationKey.toLowerCase().includes(key))
    );
  }

  expireDue(): void {
    this.api.expireDueReservations().subscribe({
      next: result => {
        this.message.set(this.t('sales.reservations.expireDueResult').replace('{count}', String(result.expiredCount)));
        this.load();
      },
      error: () => this.error.set(this.t('sales.reservations.errorExpireDue'))
    });
  }

  typeLabel(type: string): string {
    return this.localization.translate(`sales.reservations.types.${type.charAt(0).toLowerCase()}${type.slice(1)}`);
  }

  statusLabel(status: string): string {
    return this.localization.translate(`sales.reservations.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`);
  }

  onSelectionChange(selection: SalesReservationRecord[] | SalesReservationRecord | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openCreate(): void {
    void this.router.navigate(['/sales/reservations/new']);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/sales/reservations', id]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
